import { useEffect, useState } from 'react';

import { useRelayEnvironment } from 'react-relay';

import type { FomoCollaborator } from '../types';

import { fetchUsersByAaids } from './fetchUsersByAaids';

const MAX_AAIDS = 3;

export interface UseFomoCollaboratorsBatchedResult {
	collaborators: FomoCollaborator[];
	/** Whether the underlying batched fetch rejected (network/abort/5xx). Not set when AGG returned partial results. */
	error: unknown | null;
	isReady: boolean;
}

/**
 * Resolve a list of Atlassian Account IDs (AAIDs) to `FomoCollaborator[]`
 * with a single batched AGG `users(accountIds:)` request.
 *
 * - Caps input at 3 AAIDs (the avatar group's max display count).
 * - Returns immediately ready when disabled or no AAIDs.
 * - Surfaces `error` so callers can attach `errorMessage` to operational analytics.
 */
export function useFomoCollaboratorsBatched(
	aaids: ReadonlyArray<string>,
	{ enabled = true }: { enabled?: boolean } = {},
): UseFomoCollaboratorsBatchedResult {
	const environment = useRelayEnvironment();
	const capped = aaids.slice(0, MAX_AAIDS);
	const isNoop = !enabled || capped.length === 0;

	const key = isNoop ? '__noop__' : JSON.stringify(capped);

	const [state, setState] = useState<{
		collaborators: FomoCollaborator[];
		error: unknown | null;
		key: string | null;
	}>({
		collaborators: [],
		error: null,
		key: isNoop ? key : null,
	});

	useEffect(() => {
		if (isNoop) {
			setState({ collaborators: [], error: null, key });
			return;
		}

		let cancelled = false;
		fetchUsersByAaids(environment, capped).then(({ collaborators, error }) => {
			if (cancelled) return;
			setState({ collaborators, error, key });
		});

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key, isNoop, environment]);

	const loaded = state.key === key;
	return {
		collaborators: loaded ? state.collaborators : [],
		isReady: loaded,
		error: loaded ? state.error : null,
	};
}
